import { useEffect, useState } from 'react'
import { Columns, ExternalLink, FileText, Replace, Square } from 'lucide-react'
import { Button, Chip, Drawer } from '@/components/ui'
import { odooUrl } from '@/lib/utils/odoo-url'
import { documentUrl } from '@/shell/services/cofa-registry/utils/document-url'
import type { PipelineRow } from '../api/types'
import {
  STAGE_LABELS,
  STAGE_VARIANT,
  ageLabel,
  ageVariant,
} from './stage-meta'
import { ChangeProductDrawer } from '@/shell/services/cofa-registry/components/ChangeProductDrawer'
import { StageHistoryTimeline } from './StageHistoryTimeline'

// Sprint 09 Item 2c — split-screen preference key.
const SPLIT_SCREEN_PREF_KEY = 'spi-ops:cofa-pipeline:split-screen'

export interface PipelineDrawerProps {
  row: PipelineRow | null
  canAct: boolean
  /** Broader permission gate for Change Product (admin / operations /
   * regulatory). Defaults to `canAct` when not provided so legacy callers
   * keep their previous behavior. */
  canChangeProduct?: boolean
  pending?: boolean
  onClose: () => void
  onPrev?: () => void
  onNext?: () => void
  onApprove?: (row: PipelineRow) => void
  onReject?: (row: PipelineRow) => void
  onRetry?: (row: PipelineRow) => void
}

export function PipelineDrawer({
  row,
  canAct,
  canChangeProduct,
  pending,
  onClose,
  onPrev,
  onNext,
  onApprove,
  onReject,
  onRetry,
}: PipelineDrawerProps) {
  const _canChangeProduct = canChangeProduct ?? canAct
  return (
    <Drawer
      open={row !== null}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      ident={row?.lot_name}
      title={row?.product_name ?? row?.lot_name ?? ''}
      meta={
        row ? (
          <>
            <span>{row.vendor_name ?? '—'}</span>
            <span className="opacity-40">·</span>
            <Chip size="xs" variant={STAGE_VARIANT[row.stage]} dot>
              {STAGE_LABELS[row.stage]}
            </Chip>
            <span className="opacity-40">·</span>
            <span>{ageLabel(row.age_seconds)}</span>
          </>
        ) : undefined
      }
      onPrev={onPrev}
      onNext={onNext}
      headerActions={
        row?.review_url ? (
          <a
            href={row.review_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-sm border border-border-default bg-surface px-2.5 py-1 text-xs text-link hover:bg-surface-hover"
          >
            Review
            <ExternalLink size={12} />
          </a>
        ) : undefined
      }
      footer={
        row ? (
          <DrawerFooterActions
            row={row}
            canAct={canAct}
            canChangeProduct={_canChangeProduct}
            pending={pending}
            onClose={onClose}
            onApprove={onApprove}
            onReject={onReject}
            onRetry={onRetry}
          />
        ) : undefined
      }
    >
      {row ? <DrawerBody row={row} /> : null}
    </Drawer>
  )
}

function DrawerFooterActions({
  row,
  canAct,
  canChangeProduct,
  pending,
  onClose,
  onApprove,
  onReject,
  onRetry,
}: {
  row: PipelineRow
  canAct: boolean
  canChangeProduct: boolean
  pending?: boolean
  onClose: () => void
  onApprove?: (row: PipelineRow) => void
  onReject?: (row: PipelineRow) => void
  onRetry?: (row: PipelineRow) => void
}) {
  if (row.stage === 'awaiting_approval') {
    return (
      <>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <ChangeProductButton
          row={row}
          canAct={canChangeProduct}
          disabled={pending}
        />
        <Button
          variant="ghost"
          onClick={() => onReject?.(row)}
          disabled={!canAct || pending}
          title={canAct ? undefined : 'Admin or regulatory required'}
        >
          Reject
        </Button>
        <Button
          variant="primary"
          onClick={() => onApprove?.(row)}
          disabled={!canAct || pending}
          title={canAct ? undefined : 'Admin or regulatory required'}
        >
          Approve
        </Button>
      </>
    )
  }
  if (row.stage === 'generation_failed') {
    return (
      <>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={() => onRetry?.(row)}
          disabled={!canAct || pending}
          title={canAct ? undefined : 'Admin or regulatory required'}
        >
          Retry
        </Button>
      </>
    )
  }
  return (
    <>
      <ChangeProductButton
        row={row}
        canAct={canChangeProduct}
        disabled={pending}
      />
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
    </>
  )
}

/**
 * Inline button + drawer wrapper. Pulled out of ActionRow so it can manage
 * its own open/closed state and not balloon the parent's prop surface.
 * Hidden when there's no lot to reassign (defensive).
 */
function ChangeProductButton({
  row,
  canAct,
  disabled,
}: {
  row: PipelineRow
  canAct: boolean
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  if (!row.lot_id) return null
  return (
    <>
      <Button
        variant="ghost"
        onClick={() => setOpen(true)}
        disabled={!canAct || disabled}
        title={
          canAct
            ? 'Reassign this CofA to a different SPI product'
            : 'Admin, operations, or regulatory required'
        }
      >
        <Replace size={14} />
        Change Product
      </Button>
      {/* Lazy-mount the drawer so its TanStack hooks don't fire on every
          PipelineDrawer render — avoids forcing all consumers to wrap in
          QueryClientProvider just for this side-action. */}
      {open ? (
        <ChangeProductDrawer
          open={open}
          onOpenChange={setOpen}
          lotId={row.lot_id}
          lotName={row.lot_name}
          vendorId={row.vendor_id}
          vendorName={row.vendor_name}
          currentProductId={row.product_id}
          currentProductName={row.product_name}
        />
      ) : null}
    </>
  )
}

function DrawerBody({ row }: { row: PipelineRow }) {
  // Sprint 09 Item 2c — split-screen preference, persisted per-user
  // via localStorage. Default off; toggle in the body header.
  const [splitScreen, setSplitScreen] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(SPLIT_SCREEN_PREF_KEY) === '1'
    } catch {
      return false
    }
  })
  useEffect(() => {
    try {
      window.localStorage.setItem(
        SPLIT_SCREEN_PREF_KEY,
        splitScreen ? '1' : '0',
      )
    } catch {
      // localStorage may be disabled (private browsing); preference
      // resets per-session, no other failure mode.
    }
  }, [splitScreen])

  const hasGeneratedPdf = Boolean(row.spi_document_id && row.generated_filename)
  const pdfHref = hasGeneratedPdf
    ? documentUrl(row.spi_document_id!, row.generated_filename)
    : null

  const detailsCol = (
    <div className="flex flex-col gap-5">
      <section>
        <SectionLabel>Status</SectionLabel>
        <div className="flex items-center gap-2">
          <Chip size="md" variant={STAGE_VARIANT[row.stage]} dot>
            {STAGE_LABELS[row.stage]}
          </Chip>
          <Chip size="xs" variant={ageVariant(row.age_seconds)}>
            {ageLabel(row.age_seconds)}
          </Chip>
        </div>
      </section>

      <section>
        <SectionLabel>Details</SectionLabel>
        <KvGrid
          rows={[
            // Sprint 09 Item 2a — lot row → Odoo link.
            [
              'Lot',
              row.lot_id ? (
                <a
                  href={odooUrl('stock.lot', row.lot_id)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-link hover:underline"
                >
                  {row.lot_name}
                  <ExternalLink size={11} className="ml-1 inline-block" />
                </a>
              ) : (
                row.lot_name
              ),
            ],
            [
              'Product',
              row.product_id ? (
                <a
                  href={odooUrl('product.product', row.product_id)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-link hover:underline"
                >
                  {row.product_name ?? row.product_id}
                </a>
              ) : (
                (row.product_name ?? '—')
              ),
            ],
            ['Vendor', row.vendor_name ?? '—'],
            ['Uploader', row.uploader ?? '—'],
            ['Approver', row.approved_by ?? '—'],
            [
              'Generated PDF',
              row.generated_filename && row.spi_document_id ? (
                <a
                  href={documentUrl(row.spi_document_id, row.generated_filename)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-link hover:underline"
                >
                  <FileText size={11} className="mr-1 inline-block" />
                  {row.generated_filename}
                </a>
              ) : row.generated_filename ? (
                <span className="text-role-metadata">
                  {row.generated_filename}
                </span>
              ) : (
                '—'
              ),
            ],
            [
              'Conversion',
              row.conversion_id ? (
                <span className="text-role-metadata">
                  #{row.conversion_id}
                </span>
              ) : (
                '—'
              ),
            ],
          ]}
        />
      </section>

      {row.error_message ? (
        <section>
          <SectionLabel>Error</SectionLabel>
          <div className="rounded-sm border border-danger-border bg-danger-bg p-2.5">
            <div className="text-xs text-danger-text">
              {row.error_message}
            </div>
            <div className="mt-1 text-role-metadata">
              {row.error_at ? `at ${formatTimestamp(row.error_at)}` : null}
              {row.retry_count > 0 ? ` · ${row.retry_count} prior retries` : null}
            </div>
          </div>
        </section>
      ) : null}

      <section>
        <SectionLabel>Stage history</SectionLabel>
        <StageHistoryTimeline entries={row.stage_history} />
      </section>
    </div>
  )

  // Sprint 09 Item 2b — inline PDF iframe for the generated SPI CofA.
  // Sandboxed per D-4 (allow-same-origin only — no scripts, no forms).
  const pdfPanel = pdfHref ? (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-role-section-label">Generated PDF preview</span>
        <a
          href={pdfHref}
          target="_blank"
          rel="noreferrer"
          className="text-link text-xs hover:underline"
        >
          Open in new tab
        </a>
      </div>
      <iframe
        src={pdfHref}
        title={row.generated_filename ?? 'Generated CofA preview'}
        sandbox="allow-same-origin"
        className="h-full min-h-[480px] w-full rounded-sm border border-border-subtle bg-surface"
      />
    </div>
  ) : (
    <div className="flex h-full items-center justify-center text-sm text-tertiary">
      No generated PDF on this lot yet.
    </div>
  )

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar: split-screen toggle (only when there's a PDF to show). */}
      {hasGeneratedPdf ? (
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={() => setSplitScreen((v) => !v)}
            aria-pressed={splitScreen}
            title={
              splitScreen ? 'Single-column layout' : 'Side-by-side details + PDF'
            }
            className="inline-flex h-6 cursor-pointer items-center gap-1 rounded-xs border border-border-default bg-surface px-2 text-2xs text-primary hover:bg-surface-hover aria-pressed:border-accent aria-pressed:bg-accent-subtle aria-pressed:text-accent-text"
          >
            {splitScreen ? <Square size={11} /> : <Columns size={11} />}
            {splitScreen ? 'Single' : 'Split'}
          </button>
        </div>
      ) : null}
      {splitScreen && hasGeneratedPdf ? (
        <div className="grid grid-cols-2 gap-4">
          {detailsCol}
          {pdfPanel}
        </div>
      ) : (
        <>
          {detailsCol}
          {hasGeneratedPdf ? pdfPanel : null}
        </>
      )}
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 border-b border-border-subtle pb-1 text-role-section-label">
      {children}
    </div>
  )
}

function KvGrid({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-1 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-tertiary">{k}</dt>
          <dd className="text-primary tabular-nums">{v}</dd>
        </div>
      ))}
    </dl>
  )
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleString()
  } catch {
    return iso
  }
}
